import { useState, type CSSProperties } from "react";
import {
  useForm,
  type Path,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { useSession } from "@core/session/SessionProvider";
import { reportError } from "@portal/common/errorMessage";
import { Icon, ICON_PATHS } from "@core/icons";
import type { BilingualText, Welcome, WelcomeInput } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";
import {
  FieldError,
  FileUploadChip,
  LanguageSwitch,
  PreviewScrollHint,
  resolveBilingualText as text,
  useConfirmUnsavedChanges,
  useToast,
  type ContentLanguage,
} from "@portal/common";
import { useWelcome, useWelcomeMutations } from "./welcomeQueries";
import {
  CHIP_INCOMPLETE_MESSAGE,
  IMAGE_REQUIRED_MESSAGE,
  welcomeFormSchema,
  type WelcomeFormValues,
} from "./welcomeSchemas";
import { WelcomePreview } from "./WelcomePreview";

// SPEC-205 §7: mensajes literales que el cliente no puede validar antes de
// enviar (mismo criterio que `SERVER_ERROR_MESSAGES` en
// offers/OffersScreen.tsx) — respaldo si el zod de cliente se saltea.
//
// C3 (mismo hueco que HomeContentScreen.tsx/OffersScreen.tsx, code-reviewer):
// acá `INVALID_REQUEST` no viene de un campo bilingüe a medio llenar — los
// tres chips son obligatorios y esa forma la cubre `CHIP_INCOMPLETE`
// arriba— sino de una forma del payload que `welcome.service.ts:65-71`
// (`validateInput`) rechaza antes de eso: no son exactamente tres chips, o
// dos chips comparten el mismo `sortOrder`. Ninguna de las dos es
// alcanzable llenando el formulario normalmente (siempre manda tres chips
// con `sortOrder` 1/2/3), pero un código sin mensaje deja el guardado en
// silencio si alguna vez ocurre.
const INVALID_REQUEST_MESSAGE =
  "Los datos de los chips no tienen la forma esperada; recarga la página e inténtalo de nuevo";

const SERVER_ERROR_MESSAGES: Partial<Record<string, string>> = {
  IMAGE_REQUIRED: IMAGE_REQUIRED_MESSAGE,
  CHIP_INCOMPLETE: CHIP_INCOMPLETE_MESSAGE,
  INVALID_REQUEST: INVALID_REQUEST_MESSAGE,
};

// admin.jsx:110 — los doce íconos de la galería, en este orden exacto. Ya
// existen en `@core/icons/paths`; no se agrega ninguno.
const ICON_GALLERY = [
  "sparkle",
  "wrench",
  "invite",
  "star",
  "medal",
  "gift",
  "car",
  "dining",
  "doc",
  "clock",
  "pin",
  "chat",
] as const;

function defaultsFor(welcome: Welcome): WelcomeFormValues {
  // Guarda de defensa: el contrato fija minItems/maxItems=3 y el servidor
  // valida sus propias respuestas, así que hoy `welcome.chips` siempre
  // llega como arreglo — pero sin esta guarda, una respuesta malformada
  // (`chips` ausente o no-arreglo) tiraría al hacer `[...welcome.chips]` y
  // dejaría la pantalla en blanco sin estado de error. Con la guarda, el
  // formulario arranca vacío en vez de reventar.
  const chips = Array.isArray(welcome.chips) ? welcome.chips : [];
  const sorted = [...chips].sort((a, b) => a.sortOrder - b.sortOrder);
  return {
    imageUrl: welcome.imageUrl,
    imageFileId: "",
    chips: sorted.map((chip) => ({
      icon: chip.icon,
      titleEs: text(chip.title, "es"),
      titleEn: text(chip.title, "en"),
      descriptionEs: text(chip.description, "es"),
      descriptionEn: text(chip.description, "en"),
    })),
  };
}

function buildInput(values: WelcomeFormValues): WelcomeInput {
  return {
    imageUrl: values.imageUrl,
    // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo
    // nuevo en esta sesión del formulario; el backend le da prioridad
    // sobre `imageUrl` (WelcomeInput.imageFileId).
    ...(values.imageFileId ? { imageFileId: values.imageFileId } : {}),
    chips: values.chips.map((chip, index) => {
      const title: BilingualText = {
        es: chip.titleEs,
        ...(chip.titleEn ? { en: chip.titleEn } : {}),
      };
      const description: BilingualText = {
        es: chip.descriptionEs,
        ...(chip.descriptionEn ? { en: chip.descriptionEn } : {}),
      };
      return {
        sortOrder: index + 1,
        icon: chip.icon,
        title,
        description,
      };
    }),
  };
}

// RN-24/ADR-007, SPEC-205 §8 criterio 3: "un chip sin inglés muestra el
// español, nunca vacío" — es la regla que aplica `WelcomeService.resolve` del
// lado del servidor (`GET /v1/app/welcome`), reproducida acá para que el
// preview en vivo (B-01-live-preview) muestre exactamente lo que el miembro
// vería. Distinto de `resolveBilingualText` (`@portal/common`): ese helper
// devuelve el valor crudo del idioma pedido, vacío si falta — correcto para
// mostrar el campo de EDICIÓN en inglés (con su propio placeholder "Sin
// traducir…"), pero no implementa el respaldo a español que sí necesita la
// vista previa.
function resolvePreviewText(
  es: string,
  en: string,
  lang: ContentLanguage,
): string {
  if (lang === "es") return es;
  return en.trim() ? en : es;
}

function shortDateTime(value: string | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Card (admin.jsx:80-81): surface + 1px solid line, radius 18, padding 22 por
// defecto. Local a esta pantalla: es la única de `welcome-content` y no vale
// la pena extraerla hasta que otra la necesite (mismo criterio que `GPField`
// en grand-prize/GrandPrizeConfigScreen.tsx).
function WCard({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        background: tokens.surface.surface,
        border: `1px solid ${tokens.surface.line}`,
        borderRadius: 18,
        padding: 22,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// Mismo campo de texto (borde line2, fondo translúcido, 13.5px) para la URL
// de imagen y los dos campos de cada chip — extraído para no repetir el
// mismo objeto de estilos tres veces (`dup:check` es bloqueante).
const chipInputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 13px",
  borderRadius: 11,
  border: `1px solid ${tokens.surface.line2}`,
  background: "rgba(255,255,255,0.03)",
  color: tokens.text.primary,
  font: `400 13.5px ${tokens.font.ui}`,
};

// Título/Descripción de cada chip comparten la misma cabecera de campo
// (label + input) y solo cambian el `register` y el mensaje de error —
// mismo criterio de extracción que `GPField` en
// grand-prize/GrandPrizeConfigScreen.tsx, que también rotula con
// `className="label"` (admin.jsx:83-90, clase `.label`: 10.5px,
// letterSpacing 0.18em, uppercase, `--text3` — base.css:23-28).
function ChipTextField({
  label,
  field,
  placeholder,
  error,
}: {
  label: string;
  field: UseFormRegisterReturn;
  placeholder: string | undefined;
  error: string | undefined;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span className="label">{label}</span>
      <input {...field} placeholder={placeholder} style={chipInputStyle} />
      {error ? <FieldError message={error} /> : null}
    </label>
  );
}

interface WelcomeEditorProps {
  welcome: Welcome;
  canWrite: boolean;
  submitting: boolean;
  onSubmit: (input: WelcomeInput) => void;
}

function WelcomeEditor({
  welcome,
  canWrite,
  submitting,
  onSubmit,
}: WelcomeEditorProps) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
    reset,
  } = useForm<WelcomeFormValues>({ defaultValues: defaultsFor(welcome) });
  useConfirmUnsavedChanges(isDirty);
  const imageUrl = watch("imageUrl");
  const chips = watch("chips");

  const submit = (values: WelcomeFormValues) => {
    const parsed = welcomeFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path.join(".") as Path<WelcomeFormValues>, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(buildInput(parsed.data));
  };

  const discard = () => reset(defaultsFor(welcome));

  const previewChips = chips.map((chip) => ({
    icon: chip.icon,
    title: resolvePreviewText(chip.titleEs, chip.titleEn, language),
    description: resolvePreviewText(
      chip.descriptionEs,
      chip.descriptionEn,
      language,
    ),
  }));

  return (
    // Revisión en vivo 2026-09-22, punto #2: `minmax(420px,1fr)` clavaba un
    // piso de 420px para la columna del formulario que, sumado a los 402px
    // fijos de la vista previa y el gap de 26, exigía ~848px de ancho
    // disponible — CSS Grid no encoge una pista por debajo de su mínimo
    // declarado, así que con `<main>` más angosto que eso (ventana no
    // maximizada, zoom del navegador, sidebar de 262px + el padding de
    // `<main>`) el grid entero se desbordaba a la derecha: scroll horizontal
    // de página y la vista previa recortada, exactamente lo reportado. Los
    // campos de este formulario usan `width: "100%"` (`chipInputStyle` y
    // similares), así que no necesitan ese piso para verse bien; `minmax(0,
    // 1fr)` deja que la columna se encoja con el espacio real disponible en
    // vez de forzar el desborde. No cambia nada en el ancho habitual
    // (>848px disponibles): el piso nunca se activaba ahí.
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) 402px",
        gap: 26,
        alignItems: "start",
      }}
    >
      <form
        onSubmit={handleSubmit(submit)}
        noValidate
        // `minWidth: 0`: un item de grid, por defecto, no encoge por debajo
        // del ancho mínimo de su contenido (`min-width: auto`) aunque la
        // pista sea más angosta — es la otra mitad del mismo desborde de
        // arriba. Sin esto, bajar el piso de la pista a 0 no alcanzaba.
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          minWidth: 0,
        }}
      >
        <fieldset
          disabled={!canWrite}
          style={{ border: "none", margin: 0, padding: 0 }}
        >
          <WCard>
            {/* `h2`, no `h3`: corrección de una regresión propia de esta
              corrida (heading-order de axe), no deuda heredada. El `<h1>`
              de la cabecera de pantalla es nuevo en `welcome-content`
              (arriba); antes de agregarlo, este `<h3>` no violaba nada
              porque no había `h1` previo. El tamaño visual (22px) queda
              fijado inline, así que retagear a `h2` no mueve la baseline:
              el nivel semántico y el tamaño tipográfico son cosas
              distintas y no hay selector de etiqueta `h2`/`h3` en
              base.css que aplique tamaño por defecto. */}
            <h2 className="display" style={{ fontSize: 22, margin: "0 0 4px" }}>
              Imagen principal
            </h2>
            <div
              style={{
                font: `400 12px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                marginBottom: 16,
              }}
            >
              Ocupa la franja superior de la bienvenida, con degradado hacia el
              fondo. Recomendado 1200×1000 px.
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "center",
                padding: 14,
                border: `1px dashed ${tokens.surface.line2}`,
                borderRadius: 14,
                background: tokens.surface.surface,
              }}
            >
              <ImageWithPlaceholder
                src={imageUrl}
                imageStyle={{
                  width: 148,
                  height: 88,
                  objectFit: "cover",
                  objectPosition: "center 58%",
                  borderRadius: 11,
                  border: `1px solid ${tokens.surface.line}`,
                  flex: "none",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    font: `400 12px/1.5 ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                    marginBottom: 10,
                  }}
                >
                  El recorte se centra al 58% de la altura para que el frontal
                  del vehículo quede a la vista.
                </div>
                <FileUploadChip
                  entityType="welcome_screen"
                  label="Reemplazar imagen"
                  ariaLabel="Subir imagen de la pantalla de bienvenida"
                  onUploaded={(image) => {
                    // PLAN-007 Fase 4d: `imageUrl` solo para la
                    // previsualización inmediata (vigente 15 minutos); el
                    // `fileId` es lo que de verdad viaja al servidor.
                    setValue("imageUrl", image.imageUrl, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue("imageFileId", image.fileId, {
                      shouldDirty: true,
                    });
                  }}
                />
                {errors.imageUrl?.message ? (
                  <FieldError message={errors.imageUrl.message} />
                ) : null}
              </div>
            </div>
          </WCard>

          <div style={{ marginTop: 16 }}>
            <WCard>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <div>
                  <h3
                    className="display"
                    style={{ fontSize: 22, margin: "0 0 4px" }}
                  >
                    Los tres chips de valor
                  </h3>
                  <div
                    style={{
                      font: `400 12px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                      marginBottom: 18,
                    }}
                  >
                    Son exactamente tres y siempre se muestran en este orden.
                    Ícono, título y descripción son editables.
                  </div>
                </div>
                <LanguageSwitch value={language} onChange={setLanguage} />
              </div>

              {chips.map((chip, index) => {
                const titleError =
                  language === "es"
                    ? errors.chips?.[index]?.titleEs?.message
                    : undefined;
                const descriptionError =
                  language === "es"
                    ? errors.chips?.[index]?.descriptionEs?.message
                    : undefined;
                return (
                  <div
                    key={index}
                    style={{
                      padding: "16px 0",
                      borderTop: index
                        ? `1px solid ${tokens.surface.line}`
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 12,
                          background: tokens.surface.surface2,
                          border: `1px solid ${tokens.surface.line}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: tokens.silver.flat,
                          flex: "none",
                        }}
                      >
                        {ICON_PATHS[chip.icon] ? (
                          <Icon name={chip.icon} size={19} stroke={1.4} />
                        ) : null}
                      </div>
                      <span className="label">Chip {index + 1}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        marginBottom: 14,
                      }}
                    >
                      {ICON_GALLERY.map((icon) => {
                        const selected = chip.icon === icon;
                        return (
                          <button
                            key={icon}
                            type="button"
                            title={icon}
                            onClick={() =>
                              setValue(`chips.${index}.icon`, icon, {
                                shouldDirty: true,
                              })
                            }
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 11,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: selected
                                ? tokens.surface.surface3
                                : "transparent",
                              border: `1px solid ${
                                selected
                                  ? tokens.surface.line2
                                  : tokens.surface.line
                              }`,
                              color: selected
                                ? tokens.text.primary
                                : tokens.text.tertiary,
                            }}
                          >
                            <Icon name={icon} size={17} stroke={1.4} />
                          </button>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1.5fr",
                        gap: "0 16px",
                      }}
                    >
                      {/* `key={language}` remonta el input al cambiar de
                        idioma: sin él react-hook-form no reescribe el valor
                        del nodo ya registrado y el ciclo ES → EN → ES dejaba
                        el inglés bajo el campo español (QA-MTZ-002 #1; mismo
                        criterio que OffersScreen y GrandPrizeConfigScreen). */}
                      <ChipTextField
                        key={`title-${language}`}
                        label="Título"
                        field={register(
                          `chips.${index}.${
                            language === "es" ? "titleEs" : "titleEn"
                          }`,
                        )}
                        placeholder={
                          language === "en"
                            ? "Sin traducir · se mostrará en español"
                            : undefined
                        }
                        error={titleError}
                      />
                      <ChipTextField
                        key={`description-${language}`}
                        label="Descripción"
                        field={register(
                          `chips.${index}.${
                            language === "es"
                              ? "descriptionEs"
                              : "descriptionEn"
                          }`,
                        )}
                        placeholder={
                          language === "en"
                            ? "Sin traducir · se mostrará en español"
                            : undefined
                        }
                        error={descriptionError}
                      />
                    </div>
                  </div>
                );
              })}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", padding: "12px 22px" }}
                  disabled={submitting}
                >
                  {submitting ? "Guardando…" : "Guardar y publicar"}
                </button>
                <button type="button" className="btn-link" onClick={discard}>
                  Descartar cambios
                </button>
              </div>
              {/* SPEC-205 §5 y criterio 6 de §8 (resolución humana
                A-02-update-record): registro de solo lectura, no editable,
                sin campo de formulario — mismos valores tipográficos menores
                ya presentes en la pantalla. */}
              <div
                style={{
                  font: `400 11px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.disabled,
                  marginTop: 12,
                }}
              >
                {shortDateTime(welcome.updatedAt)
                  ? `Última actualización: ${shortDateTime(welcome.updatedAt)}${
                      welcome.updatedBy ? ` · ${welcome.updatedBy}` : ""
                    }`
                  : "Sin actualizaciones registradas todavía."}
              </div>
            </WCard>
          </div>
        </fieldset>
      </form>

      {/* `top: 24`: el colchón fijo que esta columna ya usaba antes de la
        fase 4b (revisión en vivo 2026-09-22). Volvió a este valor al
        revertirse STK-01 ese mismo día: la cabecera de página dejó de ser
        sticky, así que ya no hay nada opaco arriba que tapar ni una altura
        de cabecera que sumar — la columna vuelve a fijarse contra el borde
        superior de `<main>` (con su padding de 30px) más este aire propio.

        Revisión en vivo 2026-09-22, punto #2 (segunda parte): la columna
        entera (rótulo + marco de 700px + aviso + nota) puede medir más que
        el alto disponible debajo de `top` en un viewport bajo (portátil sin
        maximizar, zoom del navegador). Sin tope, el `sticky` deja de
        despegarse al llegar al borde inferior del contenedor y la parte de
        abajo de la columna queda inalcanzable: la página nunca scrollea
        más allá de lo que ya scrolleó el formulario, más largo. `maxHeight`
        + `.scr-scroll` (mismo par oculta-barra que ya usa el marco de
        adentro, base.css) le dan a la columna su propio scroll interno solo
        cuando de verdad no entra, sin agregar una barra visible ni tocar el
        caso común en el que sí entra completa. Los `24px` restados son el
        mismo colchón de abajo que el `top`, para dejarle el mismo aire al
        borde inferior del viewport. */}
      <div
        className="scr-scroll"
        style={{
          position: "sticky",
          top: 24,
          maxHeight: "calc(100vh - 48px)",
        }}
      >
        <div
          className="label"
          id="welcome-preview-label"
          style={{ marginBottom: 12 }}
        >
          Vista previa · pantalla de bienvenida
        </div>
        {/* SPEC-205, corrección de scroll: el lienzo (`WelcomePreview`) mide
          874px de contenido dentro de este marco de 700px, igual que el
          prototipo (`admin.jsx:181-185`). El prototipo lo hace recorrible
          con `.scr-scroll` (overflow-y: auto) + `.noscroll` (oculta la
          barra) — star-circle.css:67-68,88; `.scr-scroll` acá reproduce el
          mismo par. Sin esto, el tercer chip y parte del segundo quedaban
          recortados e inalcanzables. En reposo el scroll arranca arriba,
          así que la baseline visual no cambia.
          Defecto de accesibilidad preexistente (axe `scrollable-region-
          focusable`, agent-os/OBSERVATIONS.md 2026-09-19): un contenedor con
          `overflow-y: auto` y contenido que excede su alto necesita foco de
          teclado propio para que quien no usa mouse pueda desplazarlo —
          `tabIndex={0}` + `role="region"` etiquetado por el rótulo visible de
          arriba (`aria-labelledby`), sin duplicar el texto. No cambia el
          look: mismo marco, mismo recorte. */}
        <div
          className="scr-scroll"
          data-testid="welcome-preview-frame"
          role="region"
          aria-labelledby="welcome-preview-label"
          tabIndex={0}
          style={{
            width: 402,
            height: 700,
            borderRadius: 30,
            border: `1px solid ${tokens.surface.line2}`,
          }}
        >
          <WelcomePreview imageUrl={imageUrl} chips={previewChips} />
        </div>
        <PreviewScrollHint />
        {/* B-01-live-preview (evidence/human-resolution-b-01-live-preview.json):
          divergencia deliberada contra la baseline. El prototipo dice "La
          vista previa refleja lo publicado. Guarda para verla actualizada.",
          pero acá el marco refleja el formulario en edición, sin publicar —
          dejar el texto del prototipo afirmaría lo contrario de lo que la
          pantalla hace. Único texto que cambia contra la baseline. */}
        <div
          style={{
            font: `400 11px/1.5 ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 10,
          }}
        >
          La vista previa refleja los cambios sin publicar.
        </div>
      </div>
    </div>
  );
}

export function WelcomeContentScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const welcome = useWelcome();
  const mutations = useWelcomeMutations();
  const canWrite = hasPermission("content", "canEdit");

  // PLAN-009 §3: estos códigos son rechazos de servicio del guardado
  // completo (no un campo específico) — van al toast en tono `error`, con
  // `reportError` para que un código sin mapear traiga el genérico con
  // correlación en vez de quedarse mudo.
  const onSubmit = (input: WelcomeInput) => {
    mutations.edit.mutate(input, {
      onSuccess: (updated) => {
        toast.show("Pantalla de bienvenida guardada y publicada");
        for (const warning of updated.warnings ?? [])
          toast.show(warning.message, "neutral");
      },
      onError: (error) => reportError(toast, error, SERVER_ERROR_MESSAGES),
    });
  };

  // Cabecera de pantalla, patrón de MembersScreen.tsx:314-317 — entrada de
  // primer nivel de NAV, sin pestañas de grupo. Título y bajada, literales
  // del prototipo (`NAV.find(n => n.k === tab).label`, admin.jsx:3830 y
  // admin.jsx:3833).
  //
  // Volvió a ser un `<header>` plano en flujo normal: el humano revirtió
  // STK-01 el 2026-09-22 (revisión en vivo, misma sesión) — la cabecera
  // sticky vivió acá y en `home-content` un tiempo corto (`StickyScreenHeader`,
  // extraída en el punto #9 y ajustada en las fases 3a/4b), pero el pedido
  // final fue que esta pantalla se comporte como las otras ocho del portal
  // (`MembersScreen.tsx`, `RequestsScreen.tsx`, etc.): cabecera en el flujo,
  // no fija. `StickyScreenHeader` se eliminó (sin más consumidores) — ver
  // `WelcomeEditor` más abajo para la columna de vista previa, que SIGUE
  // sticky por su cuenta y ya no depende de la altura de esta cabecera.
  const header = (
    <header style={{ marginBottom: 0 }}>
      <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
        Pantalla de bienvenida
      </h1>
      <p
        style={{
          color: tokens.text.tertiary,
          margin: "2px 0 26px",
          fontSize: 13,
        }}
      >
        Imagen principal y los tres chips de valor de la primera pantalla.
      </p>
    </header>
  );

  if (welcome.isLoading) {
    return (
      <div>
        {header}
        <div style={{ padding: 24 }}>Cargando…</div>
      </div>
    );
  }
  if (welcome.isError || !welcome.data) {
    return (
      <div>
        {header}
        <div style={{ padding: 24 }}>
          <button
            type="button"
            className="btn"
            onClick={() => welcome.refetch()}
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {header}
      <WelcomeEditor
        key={welcome.data.updatedAt ?? "welcome"}
        welcome={welcome.data}
        canWrite={canWrite}
        submitting={mutations.edit.isPending}
        onSubmit={onSubmit}
      />
    </div>
  );
}
